import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Header.css";
import { AuthContext } from "../Context/AuthContext";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import DashboardIcon from "@material-ui/icons/Dashboard";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import { Button, Badge } from "../ui";

function Header() {
    const { user } = useContext(AuthContext);
    const [menuOpen, setMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    const showSearchBox = location.pathname === "/books";

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 16);
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const dashboardPath = useMemo(() => {
        if (!user) return "/signin";
        if (user.isAdmin) return "/dashboard@admin";
        if (["staff", "employee"].includes(user.userType)) {
            return "/dashboard@staff";
        }
        return "/dashboard@member";
    }, [user]);

    return (
        <header className={`app-header ${isScrolled ? "app-header--condensed" : ""}`}>
            <div className="app-header__blend" />
            <div className="app-header__inner">
                <div className="app-header__brand">
                    <Link to="/" className="app-header__logo">
                        <span className="app-header__mark">Library</span>
                        <span className="app-header__accent">Prime</span>
                    </Link>
                    <Badge variant="primary" className="app-header__badge">
                        premium experience
                    </Badge>
                </div>

                {showSearchBox && (
                    <div className="app-header__search">
                        <SearchIcon fontSize="small" />
                        <input type="text" placeholder="Search the collection" />
                    </div>
                )}

                <nav className={`app-header__nav ${menuOpen ? "is-open" : ""}`}>
                    <NavLink exact to="/" activeClassName="is-active">
                        Home
                    </NavLink>
                    <NavLink to="/books" activeClassName="is-active">
                        Books
                    </NavLink>
                    <NavLink to="/books?filter=curated" activeClassName="is-active">
                        Curated Lists
                    </NavLink>
                    <NavLink to="/books?filter=events" activeClassName="is-active">
                        Events
                    </NavLink>
                    {(user?.isAdmin || user?.permissions?.canManageUsers) && (
                        <NavLink to="/staff-management" activeClassName="is-active">
                            Staff Management
                        </NavLink>
                    )}
                </nav>

                <div className="app-header__cta">
                    {!user && (
                        <Button
                            as={Link}
                            to="/books"
                            variant="ghost"
                            size="sm"
                            leadingIcon={<LibraryBooksIcon fontSize="small" />}
                        >
                            Browse Library
                        </Button>
                    )}
                    <Button
                        as={Link}
                        to={dashboardPath}
                        variant={user ? "primary" : "secondary"}
                        size="sm"
                        leadingIcon={<DashboardIcon fontSize="small" />}
                    >
                        {user ? "Dashboard" : "Sign In"}
                    </Button>
                </div>

                <button
                    className="app-header__mobile-toggle"
                    type="button"
                    onClick={() => setMenuOpen((prev) => !prev)}
                    aria-label="Toggle navigation menu"
                >
                    {menuOpen ? <CloseIcon /> : <MenuIcon />}
                </button>
            </div>

            <div className={`app-header__mobile-sheet ${menuOpen ? "is-open" : ""}`}>
                <div className="app-header__mobile-links">
                    <NavLink exact to="/" activeClassName="is-active">
                        Home
                    </NavLink>
                    <NavLink to="/books" activeClassName="is-active">
                        Books
                    </NavLink>
                    <NavLink to="/books?filter=curated" activeClassName="is-active">
                        Curated Lists
                    </NavLink>
                    <NavLink to="/books?filter=events" activeClassName="is-active">
                        Events
                    </NavLink>
                    {(user?.isAdmin || user?.permissions?.canManageUsers) && (
                        <NavLink to="/staff-management" activeClassName="is-active">
                            Staff Management
                        </NavLink>
                    )}
                </div>
                <div className="app-header__mobile-actions">
                    {!user && (
                        <Button
                            as={Link}
                            to="/books"
                            variant="ghost"
                            leadingIcon={<EmojiEventsIcon fontSize="small" />}
                        >
                            Discover
                        </Button>
                    )}
                    <Button
                        as={Link}
                        to={dashboardPath}
                        variant={user ? "primary" : "secondary"}
                        leadingIcon={<DashboardIcon fontSize="small" />}
                    >
                        {user ? "Dashboard" : "Sign In"}
                    </Button>
                </div>
            </div>
        </header>
    );
}

export default Header;
